import { useMemo } from 'react';
import { formatNaira } from '../utils/formatters';
import { colorScales } from '../utils/colors';
import { useApp } from '../context/AppContext';

interface LegendProps {
  min: number;
  max: number;
  title?: string;
}

export function Legend({ min, max, title = 'Allocation' }: LegendProps) {
  const { state } = useApp();
  const palette = state.highContrastMode ? 'highContrast' : 'default';
  
  const colors = useMemo(() => colorScales[palette], [palette]);

  return (
    <div className="card p-3 space-y-2">
      <h4 className="text-xs font-medium text-surface-400 uppercase tracking-wider">
        {title}
      </h4>
      
      {/* Gradient bar */}
      <div className="relative h-3 rounded-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, ${colors.join(', ')})`,
          }}
        />
      </div>
      
      {/* Labels */}
      <div className="flex justify-between text-2xs text-surface-400">
        <span>{formatNaira(min, true)}</span>
        <span>{formatNaira((min + max) / 2, true)}</span>
        <span>{formatNaira(max, true)}</span>
      </div>
      
      {/* No data indicator */}
      <div className="flex items-center gap-2 pt-1 border-t border-surface-700/50">
        <div className="w-4 h-4 rounded bg-surface-800 border border-surface-600" />
        <span className="text-2xs text-surface-500">No data</span>
      </div>
    </div>
  );
}

// Compact inline legend for mobile
export function LegendCompact({ min, max }: { min: number; max: number }) {
  const { state } = useApp();
  const colors = colorScales[state.highContrastMode ? 'highContrast' : 'default'];
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-surface-500">{formatNaira(min, true)}</span>
      <div
        className="h-2 w-20 rounded-full"
        style={{
          background: `linear-gradient(to right, ${colors.join(', ')})`,
        }}
      />
      <span className="text-surface-500">{formatNaira(max, true)}</span>
    </div>
  );
}
